/* SSi website – Contact page sections.
 *
 * Mirrors the About / Mission structure: hero, a body section with a
 * couple of modules, a Join-style CTA strip. The form itself is the
 * one big "primary action" of the page; everything else is helping
 * people find the right inbox faster.
 *
 * Layout order:
 *   ContactHero      – intro
 *   ContactChannels  – three-card row: who to talk to for what
 *   ContactForm      – two-col: left = why write / what to expect,
 *                       right = the form itself
 *   ContactOffice    – address card + "in Welsh or English" note
 *   ContactJoin      – gentle CTA back into the product
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

/* ─────────────────────────────── Hero ─────────────────────────────── */
export function ContactHero({ t }) {
  const variant = (t.contactHeroIllustration || 'postcards').toLowerCase();
  return (
    <section className="ssi-contact__hero" style={contactStyles.heroWrap}>
      <div className="ssi-contact__heroInner" style={contactStyles.heroInner}>
        <div className="ssi-contact__heroCopy" style={contactStyles.heroCopy}>
          <div style={contactStyles.heroKicker}>{t.contactHeroKicker}</div>
          <h1 style={contactStyles.heroH1}>{t.contactHeroTitle}</h1>
          <p className="ssi-contact__heroLede" style={contactStyles.heroLede}>{t.contactHeroLede}</p>
        </div>
        <div className="ssi-contact__heroArt" style={contactStyles.heroArt} aria-hidden="true">
          {variant === 'bubbles'    && <HeroBubbles/>}
          {variant === 'letter'     && <HeroLetter/>}
          {variant === 'switchboard'&& <HeroSwitchboard/>}
          {(variant !== 'bubbles' && variant !== 'letter' && variant !== 'switchboard') && <HeroPostcards/>}
        </div>
      </div>
    </section>
  );
}

/* Option 1: Postcards — a small fan of overlapping postcards with greetings */
function HeroPostcards() {
  const cards = [
    { rot:-9,  x:-30, y:30,  bg:'#FDE9E5', stamp:'#C3262E', greet:'Helo!',     lang:'Welsh',   landmark:null },
    { rot: 4,  x:30,  y:0,   bg:'#FFF6DC', stamp:'#1f6b4a', greet:'Hola!',     lang:'Spanish', landmark:null },
    { rot:-3,  x:0,   y:-20, bg:'#FFFFFF', stamp:'#9b5a08', greet:'Bonjour!',  lang:'French',  landmark:'eiffel' },
  ];
  return (
    <div className="ssi-contact__heroPostcards" style={{position:'relative', width:'100%', height:380, display:'flex', alignItems:'center', justifyContent:'center'}}>
      {cards.map((c, i) => (
        <div key={i} style={{
          position:'absolute',
          width:300, height:200,
          background:c.bg,
          border:'1px solid rgba(73,3,0,.12)',
          borderRadius:6,
          boxShadow:`0 ${10 + i*4}px ${24 + i*6}px rgba(73,3,0,.${10 + i*2})`,
          transform:`translate(${c.x}px, ${c.y}px) rotate(${c.rot}deg)`,
          padding:'18px 20px',
          display:'flex', flexDirection:'column', justifyContent:'space-between',
          fontFamily:'var(--font-display)',
        }}>
          {/* postmark circle, top-right */}
          <div style={{position:'absolute', top:14, right:16, width:54, height:54, borderRadius:'50%',
            border:`1.5px dashed ${c.stamp}`, opacity:.55,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:8, color:c.stamp, letterSpacing:'.08em', fontFamily:'var(--font-body)', fontWeight:600,
            transform:'rotate(-12deg)',
          }}>
            <div style={{textAlign:'center', lineHeight:1.1}}>VIA<br/>SSI</div>
          </div>
          {/* stamp, top-left */}
          <div style={{position:'absolute', top:14, left:16, width:42, height:50,
            background:c.stamp, opacity:.85, borderRadius:2,
            display:'flex', alignItems:'center', justifyContent:'center',
            color:'#FDF5F5', fontSize:9, fontFamily:'var(--font-body)', fontWeight:700, letterSpacing:'.05em',
          }}>SSi</div>

          {/* greeting */}
          <div style={{marginTop:60}}>
            <div style={{fontSize:32, fontWeight:700, color:'#1A0F0E', letterSpacing:'-.01em', lineHeight:1}}>{c.greet}</div>
            <div style={{fontSize:11, color:'rgba(73,3,0,.55)', marginTop:6, fontFamily:'var(--font-body)', letterSpacing:'.04em', textTransform:'uppercase'}}>{c.lang}</div>
          </div>

          {/* landmark, right side */}
          {c.landmark === 'eiffel' && (
            <svg viewBox="0 0 60 110" style={{position:'absolute', right:14, bottom:18, width:48, height:88, opacity:.78}} aria-hidden="true">
              {/* base legs */}
              <path d="M 6 108 L 22 70 L 38 70 L 54 108"
                fill="none" stroke={c.stamp} strokeWidth="1.6" strokeLinecap="round"/>
              {/* inner curve under arch */}
              <path d="M 14 92 Q 30 80 46 92"
                fill="none" stroke={c.stamp} strokeWidth="1.2" strokeLinecap="round" opacity=".7"/>
              {/* first platform */}
              <line x1="18" y1="72" x2="42" y2="72" stroke={c.stamp} strokeWidth="1.4"/>
              {/* mid section */}
              <path d="M 22 70 L 26 38 M 38 70 L 34 38"
                fill="none" stroke={c.stamp} strokeWidth="1.4" strokeLinecap="round"/>
              {/* second platform */}
              <line x1="25" y1="40" x2="35" y2="40" stroke={c.stamp} strokeWidth="1.3"/>
              {/* top section */}
              <path d="M 26 38 L 28 18 M 34 38 L 32 18"
                fill="none" stroke={c.stamp} strokeWidth="1.3" strokeLinecap="round"/>
              {/* top platform */}
              <line x1="27.5" y1="18" x2="32.5" y2="18" stroke={c.stamp} strokeWidth="1.2"/>
              {/* spire */}
              <path d="M 28 18 L 30 6 L 32 18" fill="none" stroke={c.stamp} strokeWidth="1.2" strokeLinecap="round"/>
              <line x1="30" y1="6" x2="30" y2="2" stroke={c.stamp} strokeWidth="1"/>
              {/* lattice cross-hatching */}
              <path d="M 22 70 L 38 70 M 23 60 L 37 60 M 24 50 L 36 50"
                fill="none" stroke={c.stamp} strokeWidth=".7" opacity=".55"/>
              <path d="M 22 70 L 26 38 L 22 70 M 38 70 L 34 38"
                fill="none" stroke={c.stamp} strokeWidth=".5" opacity=".4"/>
            </svg>
          )}

          {/* address lines */}
          <div style={{display:'flex', flexDirection:'column', gap:4, fontFamily:'var(--font-body)', width:c.landmark ? '60%' : '100%'}}>
            <div style={{height:2, background:'rgba(73,3,0,.18)', width:'70%'}}/>
            <div style={{height:2, background:'rgba(73,3,0,.14)', width:'55%'}}/>
            <div style={{height:2, background:'rgba(73,3,0,.10)', width:'62%'}}/>
          </div>
        </div>
      ))}
    </div>
  );
}

/* Option 2: Speech bubbles — greetings in 11 languages, scattered */
function HeroBubbles() {
  const bubbles = [
    { text:'Helo',     lang:'cy', x:'12%',  y:'8%',   size:38, bg:'#C3262E', fg:'#fff',     rot:-4 },
    { text:'Hola',     lang:'es', x:'62%',  y:'4%',   size:30, bg:'#FDE9E5', fg:'#1A0F0E',  rot:6 },
    { text:'Bonjour',  lang:'fr', x:'2%',   y:'42%',  size:26, bg:'#FFF6DC', fg:'#1A0F0E',  rot:-2 },
    { text:'Ciao',     lang:'it', x:'78%',  y:'30%',  size:32, bg:'#1f6b4a', fg:'#fff',     rot:5 },
    { text:'Hallo',    lang:'de', x:'40%',  y:'24%',  size:24, bg:'#fff',     fg:'#1A0F0E', rot:-3 },
    { text:'Olá',      lang:'pt', x:'24%',  y:'66%',  size:28, bg:'#FDE9E5', fg:'#1A0F0E',  rot:8 },
    { text:'こんにちは', lang:'ja', x:'56%',  y:'62%',  size:22, bg:'#fff',    fg:'#1A0F0E', rot:-5 },
    { text:'안녕',      lang:'ko', x:'82%',  y:'72%',  size:24, bg:'#FFF6DC', fg:'#1A0F0E',  rot:3 },
    { text:'你好',      lang:'zh', x:'8%',   y:'82%',  size:22, bg:'#fff',    fg:'#1A0F0E', rot:-6 },
    { text:'مرحبا',     lang:'ar', x:'70%',  y:'88%',  size:24, bg:'#C3262E', fg:'#fff',    rot:4 },
    { text:'Hello',    lang:'en', x:'42%',  y:'88%',  size:22, bg:'#1f6b4a', fg:'#fff',     rot:-2 },
  ];
  return (
    <div style={{position:'relative', width:'100%', height:420}}>
      {bubbles.map((b, i) => (
        <div key={i} style={{
          position:'absolute', left:b.x, top:b.y,
          background:b.bg, color:b.fg,
          fontFamily:'var(--font-display)', fontWeight:700,
          fontSize:b.size, lineHeight:1,
          padding:'14px 22px',
          borderRadius:'24px 24px 24px 6px',
          border:'1px solid rgba(73,3,0,.10)',
          boxShadow:'0 8px 22px rgba(73,3,0,.10)',
          transform:`rotate(${b.rot}deg)`,
          letterSpacing:'-.005em',
          whiteSpace:'nowrap',
        }}>
          {b.text}
        </div>
      ))}
    </div>
  );
}

/* Option 3: A handwritten letter being written, with a fountain pen */
function HeroLetter() {
  return (
    <div style={{position:'relative', width:'100%', height:420, display:'flex', alignItems:'center', justifyContent:'center'}}>
      {/* envelope behind */}
      <div style={{position:'absolute', width:340, height:230, background:'#FFF6DC',
        border:'1px solid rgba(73,3,0,.18)', borderRadius:6, transform:'translate(20px, 60px) rotate(-5deg)',
        boxShadow:'0 14px 30px rgba(73,3,0,.12)',
      }}>
        {/* envelope flap */}
        <svg viewBox="0 0 340 230" style={{position:'absolute', inset:0, width:'100%', height:'100%'}}>
          <path d="M0 0 L170 130 L340 0" fill="none" stroke="rgba(73,3,0,.18)" strokeWidth="1.5"/>
        </svg>
      </div>

      {/* letter on top */}
      <div style={{position:'relative', width:300, height:340, background:'#fff',
        border:'1px solid rgba(73,3,0,.14)', borderRadius:4,
        boxShadow:'0 18px 40px rgba(73,3,0,.16)',
        padding:'32px 30px',
        transform:'rotate(2deg)',
        fontFamily:'var(--font-display)',
      }}>
        <div style={{fontSize:22, fontWeight:600, color:'#1A0F0E', letterSpacing:'-.005em', marginBottom:18}}>
          Annwyl SaySomethingin,
        </div>
        {/* handwritten lines */}
        <div style={{display:'flex', flexDirection:'column', gap:14}}>
          <div style={{height:2.5, background:'rgba(73,3,0,.55)', borderRadius:2, width:'94%'}}/>
          <div style={{height:2.5, background:'rgba(73,3,0,.45)', borderRadius:2, width:'88%'}}/>
          <div style={{height:2.5, background:'rgba(73,3,0,.35)', borderRadius:2, width:'72%'}}/>
          <div style={{height:2.5, background:'rgba(73,3,0,.28)', borderRadius:2, width:'58%'}}/>
          <div style={{height:2.5, background:'rgba(73,3,0,.18)', borderRadius:2, width:'40%'}}/>
        </div>

        <div style={{position:'absolute', bottom:24, right:30, fontFamily:'var(--font-display)',
          fontStyle:'italic', fontSize:18, color:'#C3262E', transform:'rotate(-3deg)'}}>
          ~ a learner
        </div>
      </div>

      {/* fountain pen */}
      <svg width="220" height="40" style={{position:'absolute', right:'-10px', bottom:60, transform:'rotate(-18deg)'}}>
        <defs>
          <linearGradient id="pen-body" x1="0" x2="1">
            <stop offset="0" stopColor="#3a1a18"/>
            <stop offset="1" stopColor="#1A0F0E"/>
          </linearGradient>
        </defs>
        {/* pen body */}
        <rect x="20" y="14" width="140" height="14" rx="3" fill="url(#pen-body)"/>
        {/* gold cap ring */}
        <rect x="155" y="13" width="6" height="16" fill="#c89a3c"/>
        {/* nib */}
        <polygon points="161,13 200,21 161,29" fill="#c89a3c"/>
        <polygon points="178,17 200,21 178,25" fill="#1A0F0E"/>
        {/* clip */}
        <rect x="40" y="10" width="3" height="20" fill="#c89a3c"/>
      </svg>
    </div>
  );
}

/* Option 4: Vintage switchboard — wires plug into named slots */
function HeroSwitchboard() {
  const slots = [
    { label:'Deborah',  y:30,  wired:true,  colour:'#C3262E' },
    { label:'Tom',      y:80,  wired:true,  colour:'#1f6b4a' },
    { label:'Catrin',   y:130, wired:true,  colour:'#9b5a08' },
    { label:'Stories',  y:180, wired:false, colour:'#3b5a8c' },
  ];
  return (
    <div style={{position:'relative', width:'100%', height:380, display:'flex', alignItems:'center', justifyContent:'center'}}>
      <div style={{
        width:340, height:300,
        background:'#1A0F0E',
        borderRadius:14,
        padding:'28px 26px',
        boxShadow:'0 24px 48px rgba(0,0,0,.28), inset 0 1px 0 rgba(255,255,255,.06)',
        position:'relative',
        backgroundImage:'radial-gradient(circle at 30% 20%, rgba(255,200,150,.06), transparent 60%)',
      }}>
        <div style={{fontFamily:'var(--font-display)', color:'#FFE8B0', fontSize:13, letterSpacing:'.16em',
          textTransform:'uppercase', textAlign:'center', marginBottom:22, opacity:.9}}>
          SSi · Exchange
        </div>

        {slots.map((s, i) => (
          <div key={i} style={{display:'flex', alignItems:'center', gap:14, height:36, marginBottom:8}}>
            {/* label plate */}
            <div style={{width:84, color:'#FFE8B0', fontFamily:'var(--font-body)', fontSize:13,
              fontWeight:600, letterSpacing:'.04em', opacity:.95}}>
              {s.label}
            </div>
            {/* socket */}
            <div style={{width:22, height:22, borderRadius:'50%', background:'#000',
              border:'2px solid #2a1715', position:'relative',
              boxShadow:'inset 0 2px 4px rgba(0,0,0,.6)'}}>
              {s.wired && (
                <div style={{position:'absolute', inset:5, borderRadius:'50%',
                  background: s.colour, boxShadow:`0 0 8px ${s.colour}`,
                }}/>
              )}
            </div>
            {/* the cable */}
            {s.wired && (
              <svg width="160" height="36" style={{flex:'none'}}>
                <path d={`M 0 18 C 50 ${18 + (i%2 ? 22 : -22)}, 110 ${18 + (i%2 ? -18 : 18)}, 158 18`}
                  stroke={s.colour} strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.85"/>
              </svg>
            )}
          </div>
        ))}

        {/* "no call centre" ticker tape */}
        <div style={{position:'absolute', bottom:18, left:26, right:26,
          fontSize:10, color:'rgba(255,232,176,.5)', letterSpacing:'.18em', textTransform:'uppercase',
          fontFamily:'var(--font-body)', textAlign:'center', borderTop:'1px solid rgba(255,232,176,.15)', paddingTop:10}}>
          Connecting you · {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────── Channels ─────────────────────────────── */
export function ContactChannels({ t }) {
  const cards = [
    {
      label:  t.contactCh1Label,
      title:  t.contactCh1Title,
      body:   t.contactCh1Body,
      email:  t.contactCh1Email,
      sla:    t.contactCh1Sla,
    },
    {
      label:  t.contactCh2Label,
      title:  t.contactCh2Title,
      body:   t.contactCh2Body,
      email:  t.contactCh2Email,
      sla:    t.contactCh2Sla,
    },
    {
      label:  t.contactCh3Label,
      title:  t.contactCh3Title,
      body:   t.contactCh3Body,
      email:  t.contactCh3Email,
      sla:    t.contactCh3Sla,
    },
  ];
  return (
    <section className="ssi-contact__channels" style={contactStyles.channelsWrap}>
      <div style={contactStyles.channelsHead}>
        <div style={contactStyles.kicker}>{t.contactChannelsKicker}</div>
        <h2 style={contactStyles.h2}>{t.contactChannelsTitle}</h2>
        <p style={contactStyles.leadP}>{t.contactChannelsLede}</p>
      </div>
      <ul className="ssi-contact__channelsGrid" style={contactStyles.channelsGrid}>
        {cards.map((c, i) => (
          <li key={i} style={contactStyles.chCard}>
            <div style={contactStyles.chLabel}>{c.label}</div>
            <div style={contactStyles.chTitle}>{c.title}</div>
            <p style={contactStyles.chBody}>{c.body}</p>
            <a href={`mailto:${c.email}`} style={contactStyles.chEmail}>{c.email}</a>
            <div style={contactStyles.chSla}>{c.sla}</div>
          </li>
        ))}
      </ul>

      <div className="ssi-contact__featuredCard" style={contactStyles.featuredCard}>
        <div style={contactStyles.featuredCopy}>
          <div style={contactStyles.featuredLabel}>{t.contactCh4Label}</div>
          <h3 style={contactStyles.featuredTitle}>{t.contactCh4Title}</h3>
          <p style={contactStyles.featuredBody}>{t.contactCh4Body}</p>
        </div>
        <div style={contactStyles.featuredAside}>
          <a href={`mailto:${t.contactCh4Email}`} style={contactStyles.featuredEmail}>{t.contactCh4Email}</a>
          <div style={contactStyles.featuredSla}>{t.contactCh4Sla}</div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────── Form ─────────────────────────────── */
export function ContactForm({ t }) {
  const [sent, setSent] = useState(false);
  const [topic, setTopic] = useState('learning');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const topics = [
    { id:'learning',    label: t.contactTopic1 },
    { id:'partnership', label: t.contactTopic2 },
    { id:'press',       label: t.contactTopic3 },
    { id:'story',       label: t.contactTopic4 },
    { id:'other',       label: t.contactTopic5 },
  ];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (e.target.elements.website && e.target.elements.website.value) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form: 'contact', name, email, topic, message }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
    } catch {
      setError(t.contactError ?? 'Something went wrong — please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSent(false);
    setName('');
    setEmail('');
    setMessage('');
    setTopic('learning');
    setError(null);
  };

  return (
    <section className="ssi-contact__form" style={contactStyles.formWrap}>
      <div className="ssi-contact__formInner" style={contactStyles.formInner}>
        <div className="ssi-contact__formCol1" style={contactStyles.formCol1}>
          <div style={contactStyles.kicker}>{t.contactFormKicker}</div>
          <h2 style={contactStyles.h2}>{t.contactFormTitle}</h2>
          <p style={contactStyles.p}>{t.contactFormLede}</p>
          <ul style={contactStyles.formList}>
            <li style={contactStyles.formListItem}>
              <span style={contactStyles.formListDot}>1</span>
              <span>{t.contactFormPoint1}</span>
            </li>
            <li style={contactStyles.formListItem}>
              <span style={contactStyles.formListDot}>2</span>
              <span>{t.contactFormPoint2}</span>
            </li>
            <li style={contactStyles.formListItem}>
              <span style={contactStyles.formListDot}>3</span>
              <span>{t.contactFormPoint3}</span>
            </li>
          </ul>
        </div>
        <div style={contactStyles.formCol2}>
          {sent ? (
            <div style={contactStyles.sentCard}>
              <div style={contactStyles.sentKicker}>✓ {t.contactSentKicker}</div>
              <h3 style={contactStyles.sentTitle}>{t.contactSentTitle}</h3>
              <p style={contactStyles.sentBody}>{t.contactSentBody}</p>
              <button className="btn btn--ghost" onClick={resetForm}>{t.contactSentAgain}</button>
            </div>
          ) : (
            <form className="ssi-contact__formCard" style={contactStyles.formCard} onSubmit={onSubmit}>
              <div aria-hidden="true" style={{position:'absolute', left:'-10000px', top:'auto', width:1, height:1, overflow:'hidden'}}>
                <label>Website (leave this empty)<input type="text" name="website" tabIndex={-1} autoComplete="off"/></label>
              </div>
              <div className="ssi-contact__fieldRow" style={contactStyles.fieldRow}>
                <div style={contactStyles.field}>
                  <label style={contactStyles.fLabel}>{t.contactFieldName}</label>
                  <input style={contactStyles.fInput} placeholder={t.contactFieldNamePh} required
                    value={name} onChange={e => setName(e.target.value)}/>
                </div>
                <div style={contactStyles.field}>
                  <label style={contactStyles.fLabel}>{t.contactFieldEmail}</label>
                  <input style={contactStyles.fInput} type="email" placeholder={t.contactFieldEmailPh} required
                    value={email} onChange={e => setEmail(e.target.value)}/>
                </div>
              </div>

              <div style={contactStyles.field}>
                <label style={contactStyles.fLabel}>{t.contactFieldTopic}</label>
                <div style={contactStyles.topicRow}>
                  {topics.map(tp => (
                    <button key={tp.id} type="button"
                      onClick={() => setTopic(tp.id)}
                      style={{
                        ...contactStyles.topicChip,
                        ...(topic === tp.id ? contactStyles.topicChipActive : {}),
                      }}>
                      {tp.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={contactStyles.field}>
                <label style={contactStyles.fLabel}>{t.contactFieldMessage}</label>
                <textarea style={contactStyles.fTextarea} rows={6} placeholder={t.contactFieldMessagePh} required
                  value={message} onChange={e => setMessage(e.target.value)}/>
              </div>

              {error && (
                <div role="alert" style={contactStyles.formError}>{error}</div>
              )}

              <div style={contactStyles.formFooter}>
                <div style={contactStyles.formFine}>{t.contactFine}</div>
                <button type="submit" className="btn btn--primary" disabled={submitting}
                  style={submitting ? {opacity:.6, cursor:'not-allowed'} : {}}>
                  {submitting ? (t.contactSubmitting ?? 'Sending…') : t.contactSubmit}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────── Office ─────────────────────────────── */
export function ContactOffice({ t }) {
  return (
    <section className="ssi-contact__office" style={contactStyles.officeWrap}>
      <div style={contactStyles.officeInner}>
        <div className="ssi-contact__officeCard" style={contactStyles.officeCard}>
          <div style={contactStyles.kickerOnRed}>{t.contactOfficeKicker}</div>
          <h2 style={contactStyles.h2OnRed}>{t.contactOfficeTitle}</h2>
          <div className="ssi-contact__addrGrid" style={contactStyles.addrGrid}>
            <div>
              <div style={contactStyles.addrHead}>{t.contactOfficeAddrHead}</div>
              <div style={contactStyles.addrLine}>{t.contactOfficeAddrLine1}</div>
              <div style={contactStyles.addrLine}>{t.contactOfficeAddrLine2}</div>
              <div style={contactStyles.addrLine}>{t.contactOfficeAddrLine3}</div>
              <div style={contactStyles.addrLine}>{t.contactOfficeAddrLine4}</div>
            </div>
            <div>
              <div style={contactStyles.addrHead}>{t.contactOfficeHoursHead}</div>
              <div style={contactStyles.addrLine}>{t.contactOfficeHoursLine1}</div>
              <div style={contactStyles.addrLine}>{t.contactOfficeHoursLine2}</div>
              <div style={{...contactStyles.addrLine, marginTop:14, opacity:.8}}>{t.contactOfficeHoursNote}</div>
            </div>
            <div>
              <div style={contactStyles.addrHead}>{t.contactOfficeLangHead}</div>
              <div style={contactStyles.addrLine}>{t.contactOfficeLangLine1}</div>
              <div style={{...contactStyles.addrLine, marginTop:12}}>{t.contactOfficeLangLine2}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────── Join ─────────────────────────────── */
export function ContactJoin({ t }) {
  return (
    <section className="ssi-contact__join" style={contactStyles.joinWrap}>
      <div style={contactStyles.joinInner}>
        <h2 style={contactStyles.joinH2}>{t.contactJoinTitle}</h2>
        <p style={contactStyles.joinP}>{t.contactJoinBody}</p>
        <div style={contactStyles.joinCtas}>
          <Link to="/#sample" className="btn btn--primary">{t.contactJoinCta1}</Link>
          <Link to="/about" className="btn btn--ghost" style={{border:'1px solid var(--border-strong)'}}>{t.contactJoinCta2}</Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────── Styles ─────────────────────────────── */
const contactStyles = {
  /* shared */
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  kickerOnRed: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'#FFD8D4', fontWeight:700, marginBottom:14 },
  h2: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:44, lineHeight:1.08, letterSpacing:'-.01em', margin:'0 0 18px', textWrap:'balance' },
  h2OnRed: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:40, lineHeight:1.08, letterSpacing:'-.01em', margin:'0 0 28px', color:'#fff', textWrap:'balance' },
  p: { fontSize:17, lineHeight:1.6, color:'var(--fg-1)', margin:'0 0 20px', maxWidth:'54ch' },
  leadP: { fontSize:18, lineHeight:1.55, color:'var(--grey)', margin:0, maxWidth:'58ch', textWrap:'pretty' },

  /* hero */
  heroWrap: { background:'var(--offWhite, #FDF5F5)', padding:'96px 32px 88px', borderBottom:'1px solid var(--border)' },
  heroInner: { maxWidth:1240, margin:'0 auto', display:'grid', gridTemplateColumns:'1.2fr 1fr', gap:60, alignItems:'center' },
  heroCopy: {},
  heroArt: { position:'relative', minHeight:380 },
  heroKicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:18 },
  heroH1: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:68, lineHeight:1.02, letterSpacing:'-.02em', margin:'0 0 22px', textWrap:'balance', maxWidth:'16ch' },
  heroLede: { fontFamily:'var(--font-body)', fontSize:22, lineHeight:1.5, color:'var(--grey)', margin:0, maxWidth:'58ch', textWrap:'pretty' },

  /* channels */
  channelsWrap: { padding:'96px 32px 80px' },
  channelsHead: { maxWidth:1240, margin:'0 auto 56px' },
  channelsGrid: { listStyle:'none', margin:'0 auto', padding:0, maxWidth:1240, display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:24 },
  chCard: {
    background:'#fff', border:'1px solid var(--border)', borderRadius:18,
    padding:'32px 28px 28px',
    display:'flex', flexDirection:'column', gap:0,
    transition:'border-color 200ms, box-shadow 200ms',
  },
  chLabel: { fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:18 },
  chTitle: { fontFamily:'var(--font-display)', fontSize:26, fontWeight:700, letterSpacing:'-.01em', margin:'0 0 10px', lineHeight:1.15 },
  chBody: { fontSize:15, lineHeight:1.55, color:'var(--grey)', margin:'0 0 22px', textWrap:'pretty', flex:1 },
  chEmail: {
    fontFamily:'var(--font-body)', fontSize:14.5, color:'var(--brandRed)',
    fontWeight:600, textDecoration:'none',
    padding:'10px 12px', borderRadius:10,
    background:'var(--offWhite, #FDF5F5)', border:'1px solid rgba(73,3,0,.08)',
    marginBottom:10, lineHeight:1.4,
  },
  chSla: { fontSize:12.5, color:'var(--grey)', letterSpacing:'.01em' },

  /* featured wide card – Share your story */
  featuredCard: {
    maxWidth:1240, margin:'28px auto 0', padding:'36px 40px',
    background:'var(--ssi-cream, #FBF7F0)',
    border:'1px solid var(--line)',
    borderTop:'4px solid var(--ssi-red, #C3262E)',
    display:'grid', gridTemplateColumns:'1.7fr 1fr', gap:48, alignItems:'start',
  },
  featuredCopy: { display:'flex', flexDirection:'column', gap:14 },
  featuredLabel: {
    fontSize:13, fontWeight:600, letterSpacing:'.12em', textTransform:'uppercase',
    color:'var(--ssi-red, #C3262E)',
  },
  featuredTitle: {
    fontFamily:'var(--display, "Source Serif 4", Georgia, serif)',
    fontSize:32, lineHeight:1.15, fontWeight:600, margin:0, color:'var(--ink)',
    letterSpacing:'-.005em',
  },
  featuredBody: {
    margin:0, fontSize:17, lineHeight:1.6, color:'var(--ink)', maxWidth:'62ch',
  },
  featuredAside: {
    borderLeft:'1px solid var(--line)', paddingLeft:32,
    display:'flex', flexDirection:'column', gap:10,
  },
  featuredEmail: {
    fontSize:18, fontWeight:600, color:'var(--ssi-red, #C3262E)',
    textDecoration:'none', wordBreak:'break-all',
  },
  featuredSla: { fontSize:13.5, color:'var(--grey)', lineHeight:1.5 },

  /* form */
  formWrap: { padding:'32px 32px 96px', background:'var(--offWhite, #FDF5F5)' },
  formInner: { maxWidth:1240, margin:'0 auto', display:'grid', gridTemplateColumns:'0.9fr 1.2fr', gap:64, alignItems:'start' },
  formCol1: { position:'sticky', top:28, alignSelf:'start', padding:'40px 0' },
  formCol2: {},
  formList: { listStyle:'none', margin:'20px 0 0', padding:0, display:'flex', flexDirection:'column', gap:14 },
  formListItem: { display:'flex', gap:14, alignItems:'flex-start', fontSize:15, lineHeight:1.5, color:'var(--fg-1)' },
  formListDot: {
    flexShrink:0, width:26, height:26, borderRadius:'50%',
    background:'var(--brandRed)', color:'#fff',
    fontFamily:'var(--font-display)', fontSize:14, fontWeight:700,
    display:'inline-flex', alignItems:'center', justifyContent:'center',
    marginTop:1,
  },

  formCard: {
    background:'#fff', border:'1px solid var(--border)', borderRadius:22,
    padding:'36px 36px 32px', boxShadow:'0 1px 2px rgba(73,3,0,.03)',
    display:'flex', flexDirection:'column', gap:22,
  },
  fieldRow: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 },
  field: { display:'flex', flexDirection:'column', gap:7 },
  fLabel: { fontSize:11, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--grey)', fontWeight:600 },
  fInput: {
    padding:'12px 14px', borderRadius:10, border:'1px solid var(--border-strong)',
    fontFamily:'inherit', fontSize:15, color:'var(--fg-1)', background:'#fff',
    outline:'none',
  },
  fTextarea: {
    padding:'12px 14px', borderRadius:10, border:'1px solid var(--border-strong)',
    fontFamily:'inherit', fontSize:15, color:'var(--fg-1)', background:'#fff',
    outline:'none', resize:'vertical', minHeight:140, lineHeight:1.5,
  },
  topicRow: { display:'flex', flexWrap:'wrap', gap:8, marginTop:2 },
  topicChip: {
    padding:'8px 14px', borderRadius:999, border:'1px solid var(--border-strong)',
    background:'#fff', color:'var(--fg-1)', fontSize:13.5, fontWeight:500,
    fontFamily:'inherit', cursor:'pointer',
    transition:'all 120ms',
  },
  topicChipActive: {
    background:'var(--brandRed)', borderColor:'var(--brandRed)', color:'#fff', fontWeight:600,
  },
  formFooter: { display:'flex', justifyContent:'space-between', alignItems:'center', gap:20, marginTop:4, flexWrap:'wrap' },
  formFine: { fontSize:12, color:'var(--grey)', maxWidth:'42ch', lineHeight:1.4, textWrap:'pretty' },
  formError: { fontSize:13.5, color:'var(--brandRed)', background:'#FDE9E5', borderRadius:8, padding:'10px 14px' },

  /* sent state */
  sentCard: {
    background:'#fff', border:'1px solid var(--border)', borderRadius:22,
    padding:'56px 40px', textAlign:'center',
    display:'flex', flexDirection:'column', alignItems:'center', gap:10,
  },
  sentKicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandGreen, #2f7a2e)', fontWeight:700, marginBottom:6 },
  sentTitle: { fontFamily:'var(--font-display)', fontSize:32, margin:'0 0 10px', letterSpacing:'-.01em' },
  sentBody: { fontSize:16, color:'var(--grey)', lineHeight:1.55, margin:'0 0 22px', maxWidth:'44ch', textWrap:'pretty' },

  /* office */
  officeWrap: { padding:'0 32px', background:'#fff' },
  officeInner: { maxWidth:1240, margin:'0 auto' },
  officeCard: {
    background:'var(--brandRed)', borderRadius:28, padding:'56px 56px 48px',
    color:'#fff',
    transform:'translateY(-48px)',
  },
  addrGrid: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:40, paddingTop:8 },
  addrHead: { fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', color:'#FFD8D4', fontWeight:700, marginBottom:14 },
  addrLine: { fontSize:15.5, lineHeight:1.55, color:'rgba(255,255,255,.92)' },

  /* join */
  joinWrap: { padding:'32px 32px 120px', background:'#fff' },
  joinInner: { maxWidth:760, margin:'0 auto', textAlign:'center' },
  joinH2: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:44, lineHeight:1.08, letterSpacing:'-.01em', margin:'0 0 18px', textWrap:'balance' },
  joinP: { fontSize:18, lineHeight:1.55, color:'var(--grey)', margin:'0 0 32px', textWrap:'pretty' },
  joinCtas: { display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' },
};
